<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.editTip')"
    :info="$t('components.order.actions.editTip.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.editTip.confirm'), type: 'confirm', variant: 'success', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <div v-if="order" class="pt-4 text-left ">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('components.order.actions.editTip.tipAllocation')"
          :name="$t('components.order.actions.editTip.tipAllocation')"
          class="mb-4 block"
        >
          <segment-field v-model="tipType" :options="orderTipTypeOptions" :status="status" class="border" />
        </o-field-group>

        <o-field-group
          v-if="isTipTypeSingle"
          v-slot="{ status }"
          :label="$t('components.order.actions.editTip.tipAmount')"
          :name="$t('components.order.actions.editTip.tipAmount')"
          class="mb-4 block"
          rules="currency"
        >
          <currency-field v-model="tip" :status="status" />
        </o-field-group>

        <template v-if="isTipTypePerEmployee">
          <o-field-group
            v-for="employee in employeeTips"
            :key="employee.employee.idEmployee"
            v-slot="{ status }"
            :label="$t('components.order.actions.editTip.tip', { name: employee.employee.displayName })"
            :name="$t('components.order.actions.editTip.tip', { name: employee.employee.displayName })"
            class="mb-4 block"
          >
            <currency-field v-model="employee.amount" :status="status" />
          </o-field-group>
        </template>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  SegmentField,
  CurrencyField,
} from '@/components/fields'

import { OrderTipType } from '@/_api/models/order'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:order': [value: any]
}>()

const { $api, $notification, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const tip = ref(0)
const tipType = ref<any>(null)
const employeeTips = ref<any[]>([])

const orderTipTypeOptions = computed(() => {
  return [
    { label: $i18n.t('general.single'), value: OrderTipType.single },
    { label: $i18n.t('components.order.actions.editTip.perEmployee'), value: OrderTipType.perEmployee, disabled: !hasEmployees.value, tooltip: hasEmployees.value ? undefined : $i18n.t('components.order.actions.editTip.noEmployeesAssociated') },
  ]
})

const hasEmployees = computed(() => {
  return employeeTips.value.length > 0
})

const isTipTypeSingle = computed(() => {
  return tipType.value === OrderTipType.single
})

const isTipTypePerEmployee = computed(() => {
  return tipType.value === OrderTipType.perEmployee
})

function open() {
  if (props.order) {
    tip.value = props.order.tip
    tipType.value = props.order.tipType
    employeeTips.value = props.order.data.employeeTips
  }

  dialog.value.open()
}

async function confirm() {
  emit('update:loading', true)

  try {
    let order

    if (isTipTypeSingle.value) {
      const tipVal = tip.value
      const response = await $api.order.setTipSingle(String(props.order.idOrder), tipVal)
      order = response.data
    }

    if (isTipTypePerEmployee.value) {
      const tips = employeeTips.value
      const response = await $api.order.setTipEmployee(String(props.order.idOrder), tips.map((t: any) => ({
        employeeId: String(t.employee.idEmployee),
        amount: t.amount,
      })))
      order = response.data
    }

    if (order) {
      emit('update:order', order)
      emit('update:loading', false)

      $notification.toast({
        title: $i18n.t('components.order.actions.editTip.success'),
        text: $i18n.t('components.order.actions.editTip.successInfo'),
        variant: 'success',
      })

      return true
    }
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
